/**
 * Free-shipping progress bar + celebration.
 * - <yume-free-shipping> renders the bar from its server-rendered data and animates the fill.
 * - One module-level controller listens for cart changes, reads the cart total after
 *   discounts from /cart.js, updates every bar, and celebrates only when a threshold is
 *   crossed upwards. The last reached tier lives in sessionStorage, so page loads and
 *   drawer opens never fire it, and dropping below a threshold lets it fire again.
 */
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const TIER_KEY = 'yume-fs-tier';
const FILL_KEY = 'yume-fs-fill';
const CONFETTI_COLOURS = ['#FF7A2F', '#FFC93C', '#7B5CF0', '#FF6FA5'];
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Initial cart total + thresholds, rendered on every page by layout/theme.liquid. */
const init = (() => {
  try {
    return JSON.parse(document.getElementById('yume-fs-init')?.textContent || '{}');
  } catch {
    return {};
  }
})();
const THRESHOLD_SHIPPING = Number(init.shipping) || 99900;
const THRESHOLD_SAME_DAY = Number(init.sameDay) || 149900;
const BADGE_MS = 2800;

/** Sticker from the latest crossing, re-applied if the cart re-renders while it is showing. */
let pendingBadge = null;

const storage = {
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(key, String(value));
    } catch {
      /* storage unavailable (private mode): the bar still works, it just can't remember */
    }
  },
};

class YumeFreeShipping extends HTMLElement {
  connectedCallback() {
    const total = Number(this.dataset.total) || 0;
    // Animate from the last fill this session so re-renders glide instead of jumping.
    const from = Number(storage.get(FILL_KEY));
    this.render(total, Number.isFinite(from) ? from : 0);
    controller.syncTierWithoutCelebrating(total);
    if (pendingBadge && pendingBadge.until > Date.now()) this.showBadge(pendingBadge.kind, pendingBadge.until - Date.now());
  }

  get thresholds() {
    return {
      shipping: Number(this.dataset.thresholdShipping) || 99900,
      sameDay: Number(this.dataset.thresholdSameDay) || 149900,
    };
  }

  /** @param {number} cents - shown rounded UP to the next rupee so adding it always qualifies */
  money(cents) {
    return formatMoney(Math.max(0, Math.ceil(cents / 100) * 100), this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'INR');
  }

  /**
   * @param {number} total - cart total after discounts, minor units
   * @param {number} [fromFill] - fill percentage to animate from
   */
  render(total, fromFill) {
    const { shipping, sameDay } = this.thresholds;
    let state = 'progress';
    let message = (this.dataset.msgProgress || '').replace('[amount]', this.money(shipping - total));
    if (total >= sameDay) {
      state = 'complete';
      message = this.dataset.msgComplete || '';
    } else if (total >= shipping) {
      state = 'free';
      message = (this.dataset.msgFree || '').replace('[amount]', this.money(sameDay - total));
    }

    const fill = Math.min(100, (total / sameDay) * 100);
    const text = this.querySelector('.yume-fs__text');
    const bar = this.querySelector('.yume-fs__fill');
    const track = this.querySelector('.yume-fs__track');

    this.dataset.state = state;
    this.dataset.total = String(total);
    if (text && text.textContent !== message) text.textContent = message;
    if (track) track.setAttribute('aria-valuenow', String(Math.min(Math.round(total / 100), Math.round(sameDay / 100))));

    if (bar) {
      if (typeof fromFill === 'number' && !reducedMotion.matches) {
        bar.style.transition = 'none';
        bar.style.setProperty('--yume-fs-fill', `${fromFill}%`);
        void bar.offsetWidth; // commit the start width so the next change transitions
        bar.style.transition = '';
      }
      // Set the target synchronously (no rAF) so the bar never lags behind the cart.
      bar.style.setProperty('--yume-fs-fill', `${fill}%`);
    }
    storage.set(FILL_KEY, fill.toFixed(2));
  }

  hideBadge() {
    const badge = this.querySelector('.yume-fs__badge');
    if (!badge) return;
    clearTimeout(this.badgeTimer);
    badge.hidden = true;
    badge.classList.remove('is-bouncing');
  }

  /** @param {'free' | 'complete'} kind @param {number} [duration] */
  showBadge(kind, duration = BADGE_MS) {
    const badge = this.querySelector('.yume-fs__badge');
    if (!badge) return;
    badge.textContent = kind === 'complete' ? this.dataset.badgeComplete || '' : this.dataset.badgeFree || '';
    badge.hidden = false;
    badge.classList.remove('is-bouncing');
    void badge.offsetWidth;
    badge.classList.add('is-bouncing');
    clearTimeout(this.badgeTimer);
    this.badgeTimer = setTimeout(() => {
      badge.hidden = true;
      badge.classList.remove('is-bouncing');
    }, duration);
  }
}

const controller = {
  /** @param {number} total */
  tierOf(total) {
    return total >= THRESHOLD_SAME_DAY ? 2 : total >= THRESHOLD_SHIPPING ? 1 : 0;
  },

  /** Page load / re-render: remember the tier, never celebrate. */
  syncTierWithoutCelebrating(total) {
    if (storage.get(TIER_KEY) === null) storage.set(TIER_KEY, this.tierOf(total));
  },

  /** Cart changed: update every bar, celebrate an upward crossing once. */
  apply(total) {
    // Works even before a bar exists (first item added to an empty cart: the drawer
    // re-renders with the bar slightly later).
    const bars = [...document.querySelectorAll('yume-free-shipping')];
    const tier = this.tierOf(total);
    const stored = storage.get(TIER_KEY);
    const previous = stored === null ? tier : Number(stored);
    storage.set(TIER_KEY, tier);

    const from = Number(storage.get(FILL_KEY));
    for (const bar of bars) bar.render(total, Number.isFinite(from) ? from : undefined);

    if (tier < previous) {
      pendingBadge = null;
      for (const bar of bars) bar.hideBadge();
    }

    if (tier > previous) {
      const kind = tier === 2 ? 'complete' : 'free';
      pendingBadge = { kind, until: Date.now() + BADGE_MS };
      // After "Add to cart" the drawer opens a moment later (after quick add closes):
      // celebrate once it is open so the confetti and sticker are actually visible.
      waitForOpenCartDrawer().then(() => {
        if (pendingBadge) pendingBadge.until = Date.now() + BADGE_MS;
        if (!reducedMotion.matches) confetti();
        this.showBadgeWhenReady(kind);
      });
    }
  },

  /** Show the sticker on visible bars, retrying briefly while the drawer re-renders. */
  showBadgeWhenReady(kind, attempt = 0) {
    const visible = [...document.querySelectorAll('yume-free-shipping')].filter((bar) => bar.getClientRects().length > 0);
    if (visible.length) {
      for (const bar of visible) bar.showBadge(kind);
    } else if (attempt < 14) {
      setTimeout(() => this.showBadgeWhenReady(kind, attempt + 1), 150);
    }
  },

  async refresh() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.apply(Number(cart.total_price) || 0);
    } catch {
      /* network hiccup: the server-rendered bar stays correct after the next re-render */
    }
  },
};

/** The cart drawer's <dialog>, if this page has one. */
function cartDrawerDialog() {
  return document.querySelector('cart-drawer-component')?.closest('dialog') ?? null;
}

/**
 * Resolves when the cart drawer is open, or right away when there is nothing to wait for
 * (cart page, auto-open off, drawer already open); gives up after 1.5s.
 */
function waitForOpenCartDrawer() {
  const dialog = cartDrawerDialog();
  const autoOpens = document.querySelector('cart-drawer-component[auto-open]');
  if (!dialog || dialog.open || !autoOpens) return Promise.resolve();
  return new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      if (dialog.open || Date.now() - started > 1500) resolve();
      else setTimeout(check, 60);
    };
    check();
  });
}

/**
 * Lightweight confetti (~1.4s): two bursts from the bottom corners of the open cart drawer,
 * or of the viewport on the cart page. The canvas ignores pointer events, so "Check out"
 * stays clickable throughout.
 */
function confetti() {
  const dialog = cartDrawerDialog();
  const drawer = dialog?.open ? dialog : document.querySelector('dialog.theme-drawer__dialog[open]');
  const canvas = document.createElement('canvas');
  canvas.className = 'yume-confetti';
  canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, {
    position: drawer ? 'absolute' : 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '2147483646',
  });
  // An open modal dialog sits in the top layer, so the canvas must live inside it to be visible.
  (drawer || document.body).appendChild(canvas);

  let rect = canvas.getBoundingClientRect();
  if (rect.width < 50 || rect.height < 50) {
    // Drawer not laid out yet (opening animation): size to the viewport instead.
    Object.assign(canvas.style, { position: 'fixed', width: '100vw', height: '100vh' });
    rect = canvas.getBoundingClientRect();
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.remove();
  ctx.scale(dpr, dpr);

  const w = rect.width;
  const h = rect.height;
  const count = w < 500 ? 45 : 70;
  const particles = [];
  for (const side of [0, 1]) {
    for (let i = 0; i < count; i++) {
      // Aim up and inward: 55–80° from horizontal, mirrored for the right corner.
      const angle = ((55 + Math.random() * 25) * Math.PI) / 180;
      const speed = h * (0.022 + Math.random() * 0.016);
      particles.push({
        x: side ? w : 0,
        y: h,
        vx: Math.cos(angle) * speed * (side ? -1 : 1) * (0.6 + Math.random() * 0.8),
        vy: -Math.sin(angle) * speed,
        size: 5 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.4,
        colour: CONFETTI_COLOURS[Math.floor(Math.random() * CONFETTI_COLOURS.length)],
      });
    }
  }

  const duration = 1400;
  const start = performance.now();
  const gravity = h * 0.0009;

  function frame(now) {
    const t = now - start;
    ctx.clearRect(0, 0, w, h);
    ctx.globalAlpha = t > duration - 350 ? Math.max(0, (duration - t) / 350) : 1;
    for (const p of particles) {
      p.vy += gravity;
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.spin;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.colour;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
    if (t < duration) requestAnimationFrame(frame);
    else canvas.remove();
  }
  requestAnimationFrame(frame);
  // Safety net: always clean up, even if animation frames are throttled (background tab).
  setTimeout(() => canvas.remove(), duration + 400);
}

if (!customElements.get('yume-free-shipping')) {
  // Each page load reflects the real cart: reset the remembered tier to it (never celebrates).
  if (typeof init.total === 'number') storage.set(TIER_KEY, controller.tierOf(init.total));
  customElements.define('yume-free-shipping', YumeFreeShipping);

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const settled = event.promise ? event.promise.catch(() => null) : Promise.resolve();
    settled.then(() => controller.refresh());
  });
}

// Exposed for local testing only.
export { controller as yumeFreeShippingController };
